import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Title, Text, Snackbar, HelperText, useTheme } from 'react-native-paper';
import { changePassword } from '../../api';
import { isValidPassword, passwordsMatch } from '../../utils';

const ChangePasswordScreen = ({ navigation }) => {
  const theme = useTheme();
  
  // State variables
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  // UI state variables
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Input change handler
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };
  
  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Current password validation
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Mevcut şifre gereklidir';
    }
    
    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'Yeni şifre gereklidir';
    } else if (!isValidPassword(formData.newPassword)) {
      newErrors.newPassword = 'Şifre en az 8 karakter uzunluğunda ve en az bir büyük harf, bir küçük harf ve bir sayı içermelidir';
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifre onayı gereklidir';
    } else if (!passwordsMatch(formData.newPassword, formData.confirmPassword)) {
      console.error('Passwords do not match:', formData.newPassword, formData.confirmPassword);
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Change password
  const handleChangePassword = async () => {
    // Validate form inputs
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      const passwordData = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      };

      const response = await changePassword(passwordData);
      
      // If response successful
      if (response && response.success) {
        setSnackbarMessage('Şifreniz başarıyla değiştirildi');
        setSnackbarVisible(true);
        
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        
        // Navigate back after a short delay
        setTimeout(() => {
          navigation.goBack();
        }, 2000);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      
      // Specific error for incorrect current password
      if (error.response && error.response.data && error.response.data.message === 'Mevcut şifre hatalı') {
        setSnackbarMessage('Mevcut şifreniz hatalı');
      } else {
        setSnackbarMessage('Şifre değiştirme işlemi başarısız oldu');
      }
      
      setSnackbarVisible(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderSnackbar = () => (
    <Snackbar
      visible={snackbarVisible}
      onDismiss={() => setSnackbarVisible(false)}
      duration={3000}
      style={{backgroundColor: 'rgb(125, 76, 236)'}}
      theme={{colors: {surface: '#fff'}}}
      action={{
        label: 'Tamam',
        onPress: () => setSnackbarVisible(false),
        color: '#fff'
      }}
    >
      {snackbarMessage}
    </Snackbar>
  );
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.headerContainer}>
          <Title style={styles.title}>Şifre Değiştir</Title>
          <Text style={styles.subtitle}>
            Hesap güvenliğiniz için şifrenizi düzenli olarak değiştirin
          </Text>
        </View>
        
        <View style={styles.formContainer}>
          <TextInput
            label="Mevcut Şifre *"
            value={formData.currentPassword}
            onChangeText={(value) => handleChange('currentPassword', value)}
            mode="outlined"
            secureTextEntry={!showCurrentPassword}
            error={!!errors.currentPassword}
            style={styles.input}
            left={<TextInput.Icon name="lock" />}
            right={
              <TextInput.Icon 
                name={showCurrentPassword ? "eye-off" : "eye"} 
                onPress={() => togglePasswordVisibility('current')}
              />
            }
            disabled={isLoading}
          />
          {errors.currentPassword ? <HelperText type="error">{errors.currentPassword}</HelperText> : null}
          
          {errors.newPassword ? (
            <HelperText type="error">{errors.newPassword}</HelperText>
          ) : (
            <HelperText type="info">
              Şifre en az 8 karakter uzunluğunda olmalı ve en az bir büyük harf, bir küçük harf ve bir sayı içermelidir
            </HelperText>
          )}
          <TextInput
            label="Yeni Şifre *"
            value={formData.newPassword}
            onChangeText={(value) => handleChange('newPassword', value)}
            mode="outlined"
            secureTextEntry={!showNewPassword}
            error={!!errors.newPassword}
            style={styles.input}
            left={<TextInput.Icon name="lock-plus" />}
            right={
              <TextInput.Icon 
                name={showNewPassword ? "eye-off" : "eye"} 
                onPress={() => togglePasswordVisibility('new')}
              />
            }
            disabled={isLoading}
          />
          
          
          <TextInput
            label="Yeni Şifre (Tekrar) *"
            value={formData.confirmPassword}
            onChangeText={(value) => handleChange('confirmPassword', value)}
            mode="outlined"
            secureTextEntry={!showConfirmPassword}
            error={!!errors.confirmPassword}
            style={styles.input}
            left={<TextInput.Icon name="lock-check" />}
            right={
              <TextInput.Icon 
                name={showConfirmPassword ? "eye-off" : "eye"} 
                onPress={() => togglePasswordVisibility('confirm')}
              />
            }
            disabled={isLoading}
          />
          {errors.confirmPassword ? <HelperText type="error">{errors.confirmPassword}</HelperText> : null}
          
          {/* Değiştir Butonu */}
          <Button
            mode="contained"
            onPress={handleChangePassword}
            style={styles.button}
            loading={isLoading}
            disabled={isLoading}
          >
            Şifreyi Değiştir
          </Button>
        </View>
      </ScrollView>
      
      {renderSnackbar()}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
    backgroundColor: 'rgba(125, 76, 236, 0.05)',
    borderRadius: 12,
    margin: 16,
  },
  headerContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  subtitle: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    marginTop: 4,
    textAlign: 'center',
  },
  formContainer: {
    width: '100%',
  },
  input: {
    marginBottom: 8,
  },
  button: {
    marginTop: 24,
    height: 50,
    justifyContent: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    marginBottom: 20,
  },
});

export default ChangePasswordScreen;
